// File: src/components/press/EmbeddedSongLinkCard.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useMusic } from '../../context/MusicContext';
import { useProfile } from '../../context/ProfileContext';

import playIcon from '../../assets/play.png';
import pauseIcon from '../../assets/pause.png';

/**
 * EmbeddedSongLinkCard
 * Renders an embedded song card. Uses embedded songData if present to avoid
 * secondary Firestore reads.
 */
const EmbeddedSongLinkCard = ({ songId, songBoxTitle, songData: propSongData }) => {
    const navigate = useNavigate();
    const { isDarkMode } = useProfile();
    const { currentSong, isPlaying, togglePlayPause, setQueueAndLoad } = useMusic();

    const [song, setSong] = useState(propSongData || null);
    
    // 🌟 Only load if propSongData was NOT provided
    const [isLoading, setIsLoading] = useState(!propSongData && !!songId);

    useEffect(() => {
        // If songData was already embedded in the post, use it directly (0 READS)
        if (propSongData) {
            setSong(propSongData);
            setIsLoading(false);
            return;
        }

        // Fallback: Query Firestore only for legacy posts missing embedded songData
        if (!songId) {
            setIsLoading(false);
            return;
        }

        let isMounted = true;
        const fetchSongFallback = async () => {
            try {
                const db = getFirestore();
                const songRef = doc(db, 'songs', songId);
                const songSnap = await getDoc(songRef);

                if (songSnap.exists() && isMounted) {
                    setSong({ id: songSnap.id, ...songSnap.data() });
                }
            } catch (err) {
                console.error("Error fetching song details fallback:", err);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchSongFallback();
        return () => { isMounted = false; };
    }, [songId, propSongData]);

    if (!songId && !propSongData) return null;

    const isCurrentActiveSong = currentSong?.id === (song?.id || songId);
    const isThisTrackPlaying = isCurrentActiveSong && isPlaying;

    const handlePlayClick = (e) => {
        e.stopPropagation();
        if (!song) return;

        if (isCurrentActiveSong) {
            togglePlayPause();
        } else {
            setQueueAndLoad([song], 0, 'Press Post Announcement');
        }
    };

    const handleCardNavigate = (e) => {
        e.stopPropagation();
        const targetId = song?.id || songId;
        if (targetId) {
            navigate(`/song/${targetId}`);
        }
    };

    return (
        <div 
            onClick={handleCardNavigate}
            className={`mb-6 p-3.5 rounded-2xl border transition-all duration-300 cursor-pointer group flex items-center justify-between shadow-sm hover:shadow-md ${
                isDarkMode 
                    ? "bg-zinc-900/90 border-[#ff00cc]/30 hover:border-[#ff00cc] hover:bg-zinc-900" 
                    : "bg-pink-50/50 border-pink-200/80 hover:border-[#ff00cc] hover:bg-pink-50"
            }`}
        >
            <div className="flex items-center space-x-3.5 overflow-hidden pr-3">
                {/* ALBUM ARTWORK */}
                <div className="relative w-12 h-12 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0 shadow-sm">
                    {song?.albumArtworkURL ? (
                        <img 
                            src={song.albumArtworkURL} 
                            alt={song.title || "Album Artwork"} 
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#ff00cc]/20 text-[#ff00cc] text-xs font-bold">
                            ♪
                        </div>
                    )}
                </div>

                {/* TRACK METADATA */}
                <div className="overflow-hidden">
                    {songBoxTitle && (
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#ff00cc] truncate mb-0.5">
                            {songBoxTitle}
                        </p>
                    )}
                    
                    <p className={`text-xs font-black uppercase tracking-tight truncate ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                    }`}>
                        {isLoading ? "Loading Track..." : (song?.title || "Featured Song")}
                    </p>

                    <p className="text-[10px] font-semibold text-slate-400 dark:text-zinc-400 truncate italic">
                        {song?.artistName ? `by ${song.artistName}` : "Tap to view track details"}
                    </p>
                </div>
            </div>

            {/* PLAY / PAUSE BUTTON */}
            <button
                type="button"
                onClick={handlePlayClick}
                disabled={isLoading || !song}
                className="w-10 h-10 rounded-full bg-[#ff00cc] text-white shadow-md hover:brightness-110 active:scale-95 transition-all flex items-center justify-center flex-shrink-0"
                title={isThisTrackPlaying ? "Pause Track" : "Play Track"}
            >
                <img 
                    src={isThisTrackPlaying ? pauseIcon : playIcon} 
                    alt={isThisTrackPlaying ? "Pause" : "Play"} 
                    className="w-4 h-4 invert"
                />
            </button>
        </div>
    );
};

export default EmbeddedSongLinkCard;